/* Trade Show landing page (Resin Rock store theme): the form.
 *
 * The same form exists inside RR Pro (resinrockpro.app/trade-show). Both send to
 * the trade-show-lead function in RR Pro. The rules below are a copy of
 * base44/shared/tradeShowLanding.ts in the resinrock-pro-real repo; the server
 * checks everything again, so this is for friendly errors, not security.
 *
 * Nothing here writes HTML from data: text goes in with textContent only.
 * Consent boxes start unticked and are never saved. A half-typed form is kept
 * in sessionStorage for the visit so a dropped connection doesn't wipe it.
 */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var INTERESTS = ['resin_binders', 'resin_kits', 'aggregates', 'glow_rock', 'edge_trims', 'equipment', 'training', 'not_sure'];
  var INTEREST_LABELS = { resin_binders: 'resin binders', resin_kits: 'resin bound kits', aggregates: 'aggregates', glow_rock: 'Glow Rock', edge_trims: 'edge trims', equipment: 'equipment and tools', training: 'training', not_sure: 'the right place to start' };
  var ORDER = ['first_name', 'last_name', 'phone', 'email', 'company', 'interest', 'note'];
  var DRAFT_KEY = 'rr_trade_show_draft';
  var TIMEOUT_MS = 25000;
  // Exactly what the page shows next to the boxes; sent with the lead as proof of what was agreed to.
  var SMS_DISCLOSURE = 'By checking a text box above, you agree to receive text messages from Resin Rock LLC at the number provided, including by automated means. Message frequency varies. Msg & data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of purchase. See our Privacy Policy and Terms.';
  var LABELS = {
    sms_informational: 'Text me about my enquiry, samples and call bookings',
    sms_marketing: 'Text me promotions and offers'
  };

  function cleanText(v, max) { return String(v == null ? '' : v).trim().slice(0, max); }

  function normalizePhone(raw) {
    var trimmed = String(raw == null ? '' : raw).trim();
    var digits = trimmed.replace(/[^\d]/g, '');
    if (!digits) return null;
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
    if (trimmed.charAt(0) === '+' && digits.length >= 6 && digits.length <= 15) return '+' + digits;
    return null;
  }

  function cleanEventTag(raw) {
    return String(raw == null ? '' : raw).toLowerCase().replace(/[\s_]+/g, '-').replace(/[^a-z0-9-]/g, '')
      .replace(/-{2,}/g, '-').replace(/^-+|-+$/g, '').slice(0, 60).replace(/-+$/g, '');
  }

  function validate(v) {
    var clean = {
      first_name: cleanText(v.first_name, 100),
      last_name: cleanText(v.last_name, 100),
      phone: cleanText(v.phone, 40),
      email: cleanText(v.email, 254).toLowerCase(),
      company: cleanText(v.company, 200),
      interest: INTERESTS.indexOf(v.interest) >= 0 ? v.interest : '',
      note: cleanText(v.note, 1000),
      email_consent: v.email_consent === true,
      sms_informational: v.sms_informational === true,
      sms_marketing: v.sms_marketing === true
    };
    var errors = {};
    if (!clean.first_name) errors.first_name = 'Please enter your first name.';
    if (!clean.last_name) errors.last_name = 'Please enter your last name.';
    if (!clean.phone) errors.phone = 'Please enter your mobile number.';
    else if (!normalizePhone(clean.phone)) errors.phone = "That doesn't look like a mobile number. Try something like 941 555 0100.";
    if (!clean.email) errors.email = 'Please enter your email address.';
    else if (!EMAIL_RE.test(clean.email)) errors.email = "That email address doesn't look right. Check for a typo, like name@company.com.";
    if (!clean.interest) errors.interest = "Please choose what you're interested in.";
    return { ok: Object.keys(errors).length === 0, errors: errors, clean: clean };
  }

  function post(endpoint, body) {
    var ctl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctl) ctl.abort(); }, TIMEOUT_MS);
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify(body),
      signal: ctl ? ctl.signal : undefined
    }).then(function (res) {
      clearTimeout(timer);
      return res.json().catch(function () { return {}; }).then(function (data) { return { status: res.status, data: data }; });
    }, function (err) {
      clearTimeout(timer);
      throw err;
    });
  }

  function init(root) {
    if (root.getAttribute('data-tsl-ready')) return;
    root.setAttribute('data-tsl-ready', 'true');
    var endpoint = root.getAttribute('data-endpoint');
    var form = root.querySelector('[data-tsl-form]');
    if (!endpoint || !form) return;
    var q = function (sel) { return root.querySelector(sel); };
    var summary = q('[data-tsl-summary]');
    var offlineEl = q('[data-tsl-offline]');
    var submitBtn = q('[data-tsl-submit]');
    var submitLabel = q('[data-tsl-submit-label]');
    var icon = q('[data-tsl-submit-icon]');
    var thanks = q('[data-tsl-thanks]');
    var formWrap = q('[data-tsl-formwrap]');
    var fields = {};
    ORDER.forEach(function (n) { fields[n] = form.elements[n]; });
    var boxes = { email_consent: form.elements.email_consent, sms_informational: form.elements.sms_informational, sms_marketing: form.elements.sms_marketing };
    var sendingText = root.getAttribute('data-sending') || 'Sending...';
    var sendText = submitLabel ? submitLabel.textContent : 'Send my details';
    var sending = false;
    var done = null;

    var tag = cleanEventTag(new URLSearchParams(window.location.search).get('event')) || cleanEventTag(root.getAttribute('data-event'));
    var pill = q('[data-tsl-event]');
    if (pill && tag) { pill.textContent = ' · ' + tag.replace(/-/g, ' '); pill.hidden = false; }

    // ---- draft ----
    try {
      var saved = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || 'null');
      if (saved && typeof saved === 'object') ORDER.forEach(function (n) { if (typeof saved[n] === 'string' && fields[n]) fields[n].value = saved[n]; });
    } catch (e) { /* storage blocked */ }
    function saveDraft() {
      try {
        var d = {};
        ORDER.forEach(function (n) { d[n] = fields[n].value; });
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d));
      } catch (e) { /* storage blocked */ }
    }

    // ---- errors ----
    function readValues() {
      var v = {};
      ORDER.forEach(function (n) { v[n] = fields[n].value; });
      Object.keys(boxes).forEach(function (n) { v[n] = !!boxes[n].checked; });
      return v;
    }
    function setFieldError(name, message) {
      var f = fields[name];
      var err = q('[data-tsl-err="' + name + '"]');
      if (!f || !err) return;
      var base = f.getAttribute('data-describedby') || '';
      if (message) {
        err.textContent = message;
        err.hidden = false;
        f.setAttribute('aria-invalid', 'true');
        f.setAttribute('aria-describedby', (base + ' ' + err.id).trim());
      } else {
        err.textContent = '';
        err.hidden = true;
        f.removeAttribute('aria-invalid');
        if (base) f.setAttribute('aria-describedby', base); else f.removeAttribute('aria-describedby');
      }
    }
    function showErrors(errors) {
      ORDER.forEach(function (n) { setFieldError(n, errors[n] || ''); });
      var names = ORDER.filter(function (n) { return errors[n]; });
      if (!names.length) { summary.hidden = true; return; }
      summary.textContent = '';
      var p = document.createElement('p');
      p.style.fontWeight = '600';
      p.textContent = 'Almost there. Please fix:';
      var ul = document.createElement('ul');
      names.forEach(function (n) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.href = '#' + fields[n].id;
        a.textContent = errors[n];
        a.addEventListener('click', function (ev) { ev.preventDefault(); fields[n].focus(); });
        li.appendChild(a);
        ul.appendChild(li);
      });
      summary.appendChild(p);
      summary.appendChild(ul);
      summary.hidden = false;
    }
    function showFailure(message) {
      summary.textContent = '';
      var p = document.createElement('p');
      p.textContent = message;
      summary.appendChild(p);
      summary.hidden = false;
      summary.focus();
    }

    ORDER.forEach(function (n) {
      var f = fields[n];
      f.addEventListener('input', function () { saveDraft(); if (f.getAttribute('aria-invalid')) setFieldError(n, ''); });
      f.addEventListener('change', saveDraft);
      f.addEventListener('blur', function () {
        // Checked when the person leaves it, and only once it has something in it.
        if (!f.value.trim() && n !== 'interest') return;
        var r = validate(readValues());
        if (r.errors[n]) setFieldError(n, r.errors[n]);
      });
    });

    function setOffline(off) { if (offlineEl) offlineEl.hidden = !off; }
    setOffline(navigator.onLine === false);
    window.addEventListener('online', function () { setOffline(false); });
    window.addEventListener('offline', function () { setOffline(true); });

    function setSending(on) {
      sending = on;
      submitBtn.disabled = on;
      submitBtn.setAttribute('aria-busy', on ? 'true' : 'false');
      submitLabel.textContent = on ? sendingText : sendText;
      if (icon) icon.hidden = on;
      var spin = q('[data-tsl-spin]');
      if (spin) spin.hidden = !on;
    }

    // ---- submit ----
    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      if (sending) return;
      var r = validate(readValues());
      showErrors(r.errors);
      if (!r.ok) {
        for (var i = 0; i < ORDER.length; i++) { if (r.errors[ORDER[i]]) { fields[ORDER[i]].focus(); break; } }
        return;
      }
      if (navigator.onLine === false) {
        showFailure("You're offline right now. Your details are still here. Send them as soon as you're back on Wi-Fi or signal.");
        return;
      }
      setSending(true);
      var smsChosen = r.clean.sms_informational || r.clean.sms_marketing;
      post(endpoint, {
        action: 'submit',
        site: 'shopify',
        event: tag,
        lead: r.clean,
        sms_consent_text: smsChosen ? SMS_DISCLOSURE : '',
        sms_informational_label: r.clean.sms_informational ? LABELS.sms_informational : '',
        sms_marketing_label: r.clean.sms_marketing ? LABELS.sms_marketing : '',
        website_url: (form.elements.hp_field || {}).value || ''
      }).then(function (res) {
        var d = res.data || {};
        if (res.status >= 200 && res.status < 300 && d.ok !== false) {
          try { sessionStorage.removeItem(DRAFT_KEY); } catch (e) { /* storage blocked */ }
          done = { first: r.clean.first_name, email: r.clean.email, interest: r.clean.interest, id: d.lead_id || '', booking: d.booking_url || '', phone: d.phone || '' };
          showThanks();
          return;
        }
        if (d.errors && typeof d.errors === 'object') {
          showErrors(d.errors);
          for (var i = 0; i < ORDER.length; i++) { if (d.errors[ORDER[i]]) { fields[ORDER[i]].focus(); break; } }
        } else if (d.code === 'rate_limited') {
          showFailure('Lots of people are sending details from here right now. Please wait a few minutes and try again.');
        } else {
          showFailure("We couldn't send your details. Check your connection and try again. Nothing you typed has been lost.");
        }
      }).catch(function (err) {
        showFailure(err && err.name === 'AbortError'
          ? "That's taking longer than it should. Your details are still here, so try again when your signal is better."
          : "We couldn't send your details. Check your connection and try again. Nothing you typed has been lost.");
      }).then(function () { setSending(false); });
    });

    // ---- thank-you ----
    var sampleBtn = q('[data-tsl-sample]');
    var callBtn = q('[data-tsl-call]');
    var sampleMsg = q('[data-tsl-sample-msg]');
    var callMsg = q('[data-tsl-call-msg]');
    var state = { sample: 'idle', call: 'idle' };

    function showThanks() {
      formWrap.hidden = true;
      thanks.hidden = false;
      var title = q('[data-tsl-thanks-title]');
      title.textContent = 'Thanks, ' + done.first + ". We've got your details.";
      var line = q('[data-tsl-interest]');
      if (line) {
        line.textContent = "You told us you're interested in " + (INTEREST_LABELS[done.interest] || 'Resin Rock') + '.';
        line.hidden = false;
      }
      title.focus();
    }
    function followUp(kind) {
      if (!done || state[kind] === 'sending' || state[kind] === 'done') return Promise.resolve();
      state[kind] = 'sending';
      return post(endpoint, { action: 'follow_up', kind: kind, lead_id: done.id, email: done.email }).then(function (res) {
        if (!(res.status >= 200 && res.status < 300) || (res.data && res.data.ok === false)) throw new Error('rejected');
        if (res.data && res.data.booking_url) done.booking = res.data.booking_url;
        state[kind] = 'done';
      }).catch(function () { state[kind] = 'failed'; });
    }
    function paint() {
      if (sampleBtn) {
        sampleBtn.disabled = state.sample === 'sending' || state.sample === 'done';
        sampleBtn.classList.toggle('tsl-btn--done', state.sample === 'done');
        sampleBtn.textContent = state.sample === 'done' ? 'Sample requested' : 'Request a sample';
      }
      if (sampleMsg) {
        sampleMsg.classList.toggle('tsl-bad', state.sample === 'failed');
        sampleMsg.textContent = state.sample === 'done' ? 'Got it. Our team will be in touch about where to send it.'
          : state.sample === 'failed' ? "That didn't go through. Tap the button to try again, or call us." : '';
        sampleMsg.hidden = !sampleMsg.textContent;
      }
      if (callBtn) callBtn.disabled = state.call === 'sending';
      if (callMsg) {
        callMsg.classList.toggle('tsl-bad', state.call === 'failed');
        var phone = (done && done.phone) || '';
        callMsg.textContent = state.call === 'done'
          ? (done.booking ? "Pick a time in the page that just opened. We've noted that you want a call." : "Got it. We'll call you." + (phone ? ' Want to talk now? ' + phone : ''))
          : state.call === 'failed' ? "That didn't go through. Tap the button to try again" + (phone ? ', or call ' + phone : '') + '.' : '';
        callMsg.hidden = !callMsg.textContent;
      }
    }
    // followUp() marks the button as sending before it returns, so paint() right after shows that.
    if (sampleBtn) sampleBtn.addEventListener('click', function () {
      var p = followUp('sample');
      paint();
      p.then(paint);
    });
    if (callBtn) callBtn.addEventListener('click', function () {
      // Opened first, in the tap itself, so a phone's pop-up blocker lets it through.
      if (done && done.booking && /^https:\/\//i.test(done.booking)) window.open(done.booking, '_blank', 'noopener,noreferrer');
      var p = followUp('call');
      paint();
      p.then(paint);
    });
    var another = q('[data-tsl-another]');
    if (another) another.addEventListener('click', function () {
      done = null; state.sample = 'idle'; state.call = 'idle'; paint();
      form.reset();
      showErrors({});
      thanks.hidden = true;
      formWrap.hidden = false;
      fields.first_name.focus();
    });

    // Theme editor: show the thank-you state without sending anything.
    if (root.getAttribute('data-preview-thanks') === 'true') {
      done = { first: 'Sam', email: 'sam@example.com', interest: 'resin_kits', id: '', booking: '', phone: '' };
      showThanks();
      thanks.querySelector('[data-tsl-thanks-title]').blur();
    }
  }

  // A YouTube or Vimeo video is only loaded when the visitor taps play, so the page stays light.
  document.addEventListener('click', function (ev) {
    var b = ev.target.closest && ev.target.closest('[data-tsl-play]');
    if (!b) return;
    var id = b.getAttribute('data-id') || '';
    var provider = b.getAttribute('data-provider');
    if (!/^[\w-]+$/.test(id)) return;
    var src = provider === 'vimeo' ? 'https://player.vimeo.com/video/' + id + '?autoplay=1'
      : provider === 'youtube' ? 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0&playsinline=1' : '';
    if (!src) return;
    var frame = document.createElement('iframe');
    frame.src = src;
    frame.title = b.getAttribute('data-title') || 'Video';
    frame.allow = 'autoplay; encrypted-media; picture-in-picture';
    frame.setAttribute('allowfullscreen', '');
    b.parentNode.replaceChild(frame, b);
    frame.focus();
  });

  function boot() {
    var roots = document.querySelectorAll('[data-tsl]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
